import React, { useState, useEffect } from "react";
import "./FloatingSocialShare.css";
import {
  FaLinkedinIn,
  FaFacebookF,
  FaWhatsapp,
  FaLink,
  FaCheck,
} from "react-icons/fa";
import { FaXTwitter, FaShareNodes } from "react-icons/fa6";

export default function FloatingSocialShare({ title = "360 Bytes Blog" }) {
  const [copied, setCopied] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show after slight delay for smooth appearance
    const timer = setTimeout(() => setIsVisible(true), 300);

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        const progress = Math.min(
          100,
          Math.max(0, (scrollTop / docHeight) * 100)
        );
        setScrollProgress(progress);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const getShareUrl = () => {
    return typeof window !== "undefined" ? window.location.href : "";
  };

  const openSharePopup = (url) => {
    const width = 620;
    const height = 540;
    const left = Math.max(0, (window.innerWidth - width) / 2);
    const top = Math.max(0, (window.innerHeight - height) / 2);
    window.open(
      url,
      "share-dialog",
      `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );
  };

  const handleShare = (platform) => {
    const currentUrl = getShareUrl();
    const encodedUrl = encodeURIComponent(currentUrl);
    const encodedTitle = encodeURIComponent(title);

    let shareLink = "";

    switch (platform) {
      case "linkedin":
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case "twitter":
        shareLink = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
        break;
      case "facebook":
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case "whatsapp":
        shareLink = `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`;
        break;
      default:
        break;
    }

    if (shareLink) {
      openSharePopup(shareLink);
    }
  };

  const handleCopyLink = async () => {
    const currentUrl = getShareUrl();
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(currentUrl);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = currentUrl;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  return (
    <>
      {/* Top Reading Progress Bar */}
      <div
        className="blog-reading-progress-bar"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      {/* Floating Social Media Icons Dock */}
      <aside
        className={`floating-social-dock ${isVisible ? "dock-visible" : ""}`}
        aria-label="Share this article"
      >
        <div className="floating-social-header" title="Share this article">
          <FaShareNodes className="floating-share-icon" />
          <span className="floating-share-text">SHARE</span>
        </div>

        <div className="floating-social-divider" />

        <div className="floating-social-buttons">
          {/* LinkedIn */}
          <button
            type="button"
            className="floating-social-btn btn-linkedin"
            onClick={() => handleShare("linkedin")}
            aria-label="Share on LinkedIn"
          >
            <FaLinkedinIn />
            <span className="floating-social-tooltip">LinkedIn</span>
          </button>

          {/* X / Twitter */}
          <button
            type="button"
            className="floating-social-btn btn-twitter"
            onClick={() => handleShare("twitter")}
            aria-label="Share on X (Twitter)"
          >
            <FaXTwitter />
            <span className="floating-social-tooltip">X / Twitter</span>
          </button>

          {/* Facebook */}
          <button
            type="button"
            className="floating-social-btn btn-facebook"
            onClick={() => handleShare("facebook")}
            aria-label="Share on Facebook"
          >
            <FaFacebookF />
            <span className="floating-social-tooltip">Facebook</span>
          </button>

          {/* WhatsApp */}
          <button
            type="button"
            className="floating-social-btn btn-whatsapp"
            onClick={() => handleShare("whatsapp")}
            aria-label="Share on WhatsApp"
          >
            <FaWhatsapp />
            <span className="floating-social-tooltip">WhatsApp</span>
          </button>

          {/* Copy Link */}
          <button
            type="button"
            className={`floating-social-btn btn-copy ${copied ? "is-copied" : ""}`}
            onClick={handleCopyLink}
            aria-label={copied ? "Link Copied" : "Copy Article Link"}
          >
            {copied ? <FaCheck /> : <FaLink />}
            <span className="floating-social-tooltip">
              {copied ? "Copied!" : "Copy Link"}
            </span>
          </button>
        </div>

        {/* Floating Copied Toast Alert */}
        {copied && (
          <div className="floating-copy-toast" role="status" aria-live="polite">
            <FaCheck /> Link copied to clipboard!
          </div>
        )}
      </aside>
    </>
  );
}
